import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  AspectRatio,
  Box,
  HStack,
  Icon as ChakraIcon,
  Link,
  SimpleGrid,
  Spacer,
  Stack,
  Tag,
  Text,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import { RevealVertically } from "@raiden/library/components/Animations/RevealVertically";
import PampaImage from "@raiden/library/components/PampaImage";
import { FILES_MEDIAS } from "@raiden/library/constants/files";
import {
  POSTS_TYPE_VALUE_ARTICLE,
  POSTS_TYPE_VALUE_ASSOCIATION,
  POSTS_TYPE_VALUE_EVENT,
  POSTS_TYPE_VALUE_PAGE,
} from "@raiden/library/constants/posts";
import { RESOURCES_VISIBILITY_VALUE_PRIVATE } from "@raiden/library/constants/resources";
import { apiGetErrorDetail } from "@raiden/library/helpers/api";
import getFileSize from "@raiden/library/helpers/files/getFileSize";
import { useApi } from "@raiden/library/hooks/useApi";
import { useConfigurationMenu } from "@raiden/library/hooks/useConfigurationMenu";
import useRequest from "@raiden/library/hooks/useRequest";
import useTranslate from "@raiden/library/hooks/useTranslate";
import downloadBlob from "@raiden/library/libraries/utils/downloadBlob";
import generateApiUrl from "@raiden/library/libraries/utils/generateApiUrl";
import generateFrontPath from "@raiden/library/libraries/utils/generateFrontPath";
import { useMemo } from "react";
import { FormattedDate, FormattedMessage, useIntl } from "react-intl";
import { CenteredContent } from "../../../../components/CenteredContent";
import { DataHandler } from "../../../../components/DataHandler";
import { Icon } from "../../../../components/Icon";
import { PageLayout } from "../../../../components/PageLayout";
import { getPagesIdsFromMenu } from "../../../../helpers/menus/getIdsFromMenu";
import { getPostPath } from "../../../../helpers/posts/getPostPath";
import { PostsContentView } from "../ContentView";
import { PostSummary } from "../Summary";
import { PostViewSuggestions } from "./suggestions";

/**
 * Get next post page ID
 * @param {object} param
 * @param {Array<number>} param.pagesIds
 * @param {number} param.currentPageId
 * @return {number | null}
 */
function getNextPageId({ pagesIds, currentPageId }) {
  const currentPageIndex = pagesIds.indexOf(currentPageId);
  if (currentPageIndex === -1 || currentPageIndex === pagesIds.length - 1) {
    return null;
  }
  return pagesIds[currentPageIndex + 1];
}

/**
 * @typedef {Object} Props
 * @property {import("swr").SWRResponse<import("@raiden/library/types/Api/ApiResponse").ApiResponse<import("@raiden/library/types/Post").Post>>} swrResponsePost
 */
/**
 * @param {Props} props
 */
export function PostView({ swrResponsePost }) {
  const { menu } = useConfigurationMenu({ alias: "header" });

  const intl = useIntl();

  const translate = useTranslate();

  const post = swrResponsePost.data?.data;
  const cover = post?.current_revision?.cover;
  const caption = cover?.caption ? translate(cover.caption) ?? "" : "";
  const title = translate(swrResponsePost.data?.data.current_revision?.title);

  const isPage = post?.type === POSTS_TYPE_VALUE_PAGE;
  const isArticle = post?.type === POSTS_TYPE_VALUE_ARTICLE;
  const isAssociation = post?.type === POSTS_TYPE_VALUE_ASSOCIATION;
  const isEvent = post?.type === POSTS_TYPE_VALUE_EVENT;

  const breadcrumbItems = useMemo(() => {
    /** @type {import("../../../../components/Breadcrumb").BreadcrumbItem | undefined} */
    let postTypeBreadcrumbItem = undefined;

    switch (swrResponsePost.data?.data.type) {
      case POSTS_TYPE_VALUE_ARTICLE:
        postTypeBreadcrumbItem = {
          title: intl.formatMessage({ defaultMessage: "Les actualités" }),
          url: generateFrontPath({ id: "@front.articles" }),
        };
        break;

      case POSTS_TYPE_VALUE_EVENT:
        postTypeBreadcrumbItem = {
          title: intl.formatMessage({ defaultMessage: "L'agenda" }),
          url: generateFrontPath({ id: "@front.events" }),
        };
        break;

      case POSTS_TYPE_VALUE_ASSOCIATION:
        postTypeBreadcrumbItem = {
          title: intl.formatMessage({ defaultMessage: "Les associations" }),
          url: generateFrontPath({ id: "@front.associations" }),
        };
        break;

      default:
        break;
    }

    /** @type {import("../../../../components/Breadcrumb").BreadcrumbItem[]} */
    const items = [
      {
        title: intl.formatMessage({
          defaultMessage: "Accueil",
        }),
        url: generateFrontPath({ id: "@front.home" }),
      },
      ...(postTypeBreadcrumbItem ? [postTypeBreadcrumbItem] : []),
      ...(post && title
        ? [
            {
              title: title,
              url: getPostPath({
                postId: post.id,
                postType: post.type,
                postSlug: post.current_revision?.slug ?? null,
                translate,
              }),
            },
          ]
        : []),
    ];
    return items;
  }, [intl, post, swrResponsePost.data?.data.type, title, translate]);

  const pagesIds = getPagesIdsFromMenu(menu);
  const nextPage =
    post?.id &&
    getNextPageId({
      pagesIds,
      currentPageId: post.id,
    });

  /**
   * swrResponse utilisée pour charger les suggestions de posts.
   * @type {import("@raiden/library/hooks/useApi").UseApi<import("@raiden/library/types/Post").Post[]>}
   */
  const { swrResponse: swrResponsePosts } = useApi(
    nextPage || !isPage
      ? generateApiUrl({
          id: "@api.posts.viewAny",
          query: {
            exclude_id: [post?.id],
            only_id: isPage && nextPage ? [nextPage] : [],
            page: 1,
            per_page: isAssociation ? 3 : 6,
            category_id: !isPage
              ? post?.categories?.map((category) => category.id) ?? []
              : [],
            type: [swrResponsePost.data?.data.type],
            fields: [
              "post.current_revision",
              "post.categories",
              "post.revision.cover",
              "post.revision.files",
            ],
          },
        })
      : null,
  );

  const { request, toastSuccess } = useRequest();

  /**
   * Gère la soumission du formulaire.
   * @param {Object} param0
   * @param {number} param0.fileId
   * @param {string} param0.filename
   */
  function handleClickDownload({ fileId, filename }) {
    request(
      generateApiUrl({
        id: "@api.files.download",
        parameters: {
          fileId,
        },
      }),
      {
        method: "GET",
      },
      {
        outputMode: "file",
      },
    )
      .then(function (response) {
        if (response?.data?.contentType === "blob") {
          downloadBlob({
            blob: response?.data?.blob,
            filename: response?.data?.filename ?? filename,
          });
          toastSuccess({
            title: intl.formatMessage({
              defaultMessage: "Le téléchargement va débuter.",
            }),
          });
        }
      })
      .catch((response) => {
        toastSuccess({
          title: response?.body?.errors?.[0]?.detail
            ? apiGetErrorDetail({ error: response })
            : intl.formatMessage({
                defaultMessage:
                  "Une erreur est survenue lors du téléchargement du fichier.",
              }),
          status: "error",
        });
      });
  }

  return (
    <PageLayout
      title={title}
      breadcrumbItems={breadcrumbItems}
      leftAddon={
        post ? (
          <Stack
            spacing="0"
            direction={{ base: "column", md: "row", lg: "column" }}>
            <Box
              w="full"
              maxW={{ base: "none", md: "387px" }}
              backgroundColor="blackAlpha.50">
              <AspectRatio ratio={387 / 217}>
                <PampaImage
                  image={post?.current_revision?.cover}
                  placeholderSrc={generateFrontPath({
                    id: "@front.internal-assets",
                    parameters: { filePath: "placeholder.svg" },
                    includeBasePath: true,
                  })}
                  mode="crop"
                  format="detail"
                  w="full"
                  alt={caption}
                />
              </AspectRatio>
            </Box>

            <Box flexGrow={1}>
              <PostSummary post={post} />
            </Box>
          </Stack>
        ) : null
      }
      bottomAddon={
        <>
          {post?.current_revision?.files &&
            (post?.current_revision?.files ?? []).length > 0 && (
              <Box py="1.5rem" px="2rem" bg="brandPrimary.100">
                <CenteredContent>
                  <Text variant="h3" color="brandPrimary.700">
                    <FormattedMessage
                      defaultMessage="{nbFile} document{nbFile, plural, one {} other {s}} disponible{nbFile, plural, one {} other {s}} à télécharger"
                      values={{ nbFile: post?.current_revision.files.length }}
                    />
                  </Text>

                  <SimpleGrid
                    columns={{ base: 1, md: 2, lg: 3 }}
                    spacing=".5rem"
                    mt=".5rem">
                    {post?.current_revision.files.map((file) => (
                      <HStack
                        key={file.id}
                        as="button"
                        py=".2rem"
                        px=".5rem"
                        borderRadius=".25rem"
                        _hover={{ bg: "brandPrimary.50" }}
                        onClick={() =>
                          handleClickDownload({
                            fileId: file.id,
                            filename: file.filename,
                          })
                        }>
                        <Box>
                          <ChakraIcon
                            as={FILES_MEDIAS[file.mime_type].icon}
                            fontSize="2rem"
                            color="brandPrimary.700"
                          />
                        </Box>

                        <Box>
                          <Text
                            fontSize="1.125rem"
                            fontWeight={500}
                            align="left">
                            {file.filename}
                          </Text>

                          <Text
                            as={HStack}
                            gap="1rem"
                            fontSize=".875rem"
                            fontWeight={500}
                            color="gray.400">
                            <WrapItem>
                              {getFileSize({ size: file.filesize, intl })}
                            </WrapItem>

                            <WrapItem _hover={{ textDecoration: "underline" }}>
                              <FormattedMessage defaultMessage="Télécharger" />
                            </WrapItem>
                          </Text>
                        </Box>
                      </HStack>
                    ))}
                  </SimpleGrid>
                </CenteredContent>
              </Box>
            )}

          {(swrResponsePosts.data?.data ?? []).length > 0 && (
            <Box py="1.5rem" px="2rem" bg="brandPrimary.700">
              <CenteredContent>
                <Text variant="h3" color="white">
                  <FormattedMessage
                    defaultMessage="{type, select, event {Événements similaires} association {Associations similaires} other {À lire également}}"
                    values={{
                      type: post?.type,
                    }}
                  />
                </Text>

                <Stack
                  mt=".5rem"
                  direction={{
                    base: "row",
                    md: "column",
                    lg: "row",
                  }}
                  spacing="0"
                  gap={isAssociation ? "1.5rem" : ".5rem"}
                  wrap="wrap">
                  <PostViewSuggestions
                    swrResponsePosts={swrResponsePosts}
                    type={post?.type}
                    cardMaxWidth={isAssociation ? "32%" : "48%"}
                    caption={caption}
                  />
                </Stack>
              </CenteredContent>
            </Box>
          )}
        </>
      }>
      <RevealVertically
        isVisible={Boolean(
          post && post.visibility === RESOURCES_VISIBILITY_VALUE_PRIVATE,
        )}>
        <Box px="1rem">
          <CenteredContent>
            <Alert status="warning">
              <AlertIcon />

              <AlertTitle>
                <FormattedMessage defaultMessage="Page privé" />
              </AlertTitle>

              <AlertDescription>
                <FormattedMessage defaultMessage="Vous êtes en train de visualiser une page non disponible au public." />
              </AlertDescription>
            </Alert>
          </CenteredContent>
        </Box>
      </RevealVertically>

      <DataHandler
        swrResponse={swrResponsePost}
        keepDataOnRevalidation={true}
        wrapper={(children) => (
          <Box px="1rem">
            <CenteredContent>{children}</CenteredContent>
          </Box>
        )}>
        {({ data: post }) => (
          <>
            <Box px="1rem">
              <CenteredContent>
                <Wrap spacing="1rem" mt="1rem">
                  {post.categories?.map((category) => (
                    <Tag
                      variant="outline"
                      size="lg"
                      borderRadius=".25rem"
                      color="gray.400"
                      key={category.id}>
                      <Text color="gray.600">{translate(category.name)}</Text>
                    </Tag>
                  ))}

                  {isArticle && post.publication_date && (
                    <Box
                      backgroundColor="brandPrimary.700"
                      borderRadius=".25rem"
                      opacity={0.9}
                      py="6px"
                      px="8px">
                      <Text
                        fontSize="1.125rem"
                        fontWeight={400}
                        lineHeight="1.375rem"
                        color="white">
                        <FormattedMessage
                          defaultMessage="Publié le {date}"
                          values={{
                            date: intl.formatDate(post.publication_date, {
                              day: "numeric",
                              month: "long",
                              year: "numeric",
                            }),
                          }}
                        />
                      </Text>
                    </Box>
                  )}

                  {isEvent && (
                    <>
                      {post.current_revision?.event_start_at && (
                        <Box
                          backgroundColor="brandPrimary.700"
                          borderRadius=".25rem"
                          opacity={0.9}
                          py="6px"
                          px="8px">
                          {post.current_revision?.event_end_at ? (
                            <HStack>
                              <Text
                                as={HStack}
                                fontSize="1rem"
                                fontWeight={500}
                                color="white">
                                <FormattedDate
                                  value={post.current_revision.event_start_at}
                                  day="numeric"
                                  month="short"
                                  year="numeric"
                                />

                                <Icon
                                  icon="ms_chevron_right"
                                  size="1.25rem"
                                  color="white"
                                />

                                <FormattedDate
                                  value={post.current_revision.event_end_at}
                                  day="numeric"
                                  month="short"
                                  year="numeric"
                                />
                              </Text>
                            </HStack>
                          ) : (
                            <Text
                              fontSize="1rem"
                              fontWeight={500}
                              color="white">
                              <FormattedDate
                                value={post.current_revision.event_start_at}
                                day="numeric"
                                month="long"
                                year="numeric"
                              />
                            </Text>
                          )}
                        </Box>
                      )}

                      {post.current_revision?.event_info && (
                        <Box
                          backgroundColor="brandPrimary.700"
                          borderRadius=".25rem"
                          opacity={0.9}
                          py="6px"
                          px="8px">
                          <Text fontSize="1rem" fontWeight={500} color="white">
                            <FormattedMessage
                              defaultMessage="Horaires: {info}"
                              values={{
                                info: post.current_revision.event_info,
                              }}
                            />
                          </Text>
                        </Box>
                      )}

                      {post.current_revision?.event_place && (
                        <Box
                          backgroundColor="brandPrimary.700"
                          borderRadius=".25rem"
                          opacity={0.9}
                          py="6px"
                          px="8px">
                          <Text fontSize="1rem" fontWeight={500} color="white">
                            <FormattedMessage defaultMessage="Lieu: " />

                            <Text
                              as="span"
                              pl=".25rem"
                              textDecoration="underline"
                              fontSize="inherit"
                              color="inherit">
                              {post.current_revision.event_place}
                            </Text>
                          </Text>
                        </Box>
                      )}
                    </>
                  )}

                  {isAssociation && post.current_revision?.asso_email && (
                    <>
                      <Spacer />

                      <Link
                        href={`mailto:${post.current_revision.asso_email}`}
                        backgroundColor="brandPrimary.700"
                        borderRadius=".25rem"
                        py="6px"
                        px="8px"
                        _hover={{
                          bg: "brandPrimary.600",
                          textDecoration: "none",
                        }}>
                        <Text fontSize="1rem" fontWeight={400} color="white">
                          <FormattedMessage defaultMessage="Contacter par email" />
                        </Text>
                      </Link>
                    </>
                  )}
                </Wrap>
              </CenteredContent>
            </Box>

            <Box mt="2rem">
              <CenteredContent
                maxWidth={({ defaultMaxWidth }) =>
                  `calc(${defaultMaxWidth} + 32px)`
                }>
                <PostsContentView post={post} />
              </CenteredContent>
            </Box>
          </>
        )}
      </DataHandler>
    </PageLayout>
  );
}
